<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <!-- cv-tool.com Logo -->
      <div class="logo-container">
        <img 
          src="../assets/icons/cv-tool-gold.png" 
          alt="cv-tool.com" 
          class="logo"
        />
      </div>
      
      <h2 class="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
        Create your account
      </h2>
      <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
        Or
        <a href="/login" class="font-medium text-primary-600 hover:text-primary-500">
          sign in to your account
        </a>
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <!-- Success state - show after successful registration -->
        <div v-if="registrationSuccess" class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
            <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            Account Created Successfully!
          </h3>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            We've sent a confirmation email to <strong>{{ registeredEmail }}</strong>. 
            Please check your inbox and click the confirmation link to activate your account.
          </p>
          <div class="mt-6 space-y-3">
            <a
              href="/login"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Go to Sign In
            </a>
            <button
              @click="resendConfirmation"
              :disabled="isResending"
              class="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <svg 
                v-if="isResending" 
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isResending ? 'Sending...' : 'Resend Confirmation Email' }}
            </button>
          </div>
        </div>

        <!-- Registration form -->
        <form v-else class="space-y-6" @submit.prevent="handleSubmit">
          <!-- Alert for errors -->
          <div v-if="error" class="rounded-md bg-red-50 dark:bg-red-900 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
                  {{ error }}
                </h3>
              </div>
            </div>
          </div>

          <!-- First Name -->
          <div>
            <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              First Name
            </label>
            <div class="mt-1">
              <input 
                id="first_name" 
                v-model="firstName" 
                name="first_name" 
                type="text" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <!-- Last Name -->
          <div>
            <label for="last_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Last Name
            </label>
            <div class="mt-1">
              <input 
                id="last_name" 
                v-model="lastName" 
                name="last_name" 
                type="text" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email address
            </label>
            <div class="mt-1">
              <input 
                id="email" 
                v-model="email" 
                name="email" 
                type="email" 
                autocomplete="email" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <div class="mt-1">
              <input 
                id="password" 
                v-model="password" 
                name="password" 
                type="password" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <!-- Password Confirmation -->
          <div>
            <label for="password_confirmation" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirm Password
            </label>
            <div class="mt-1">
              <input 
                id="password_confirmation" 
                v-model="passwordConfirmation" 
                name="password_confirmation" 
                type="password" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <svg 
                v-if="isLoading" 
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isLoading ? 'Creating account...' : 'Create account' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import logoUrl from '../assets/icons/cv-tool-gold.png'

export default {
  name: 'Signup',
  
  setup() {
    const router = useRouter()
    const firstName = ref('')
    const lastName = ref('')
    const email = ref('')
    const password = ref('')
    const passwordConfirmation = ref('')
    const isLoading = ref(false)
    const error = ref('')
    const registrationSuccess = ref(false)
    const registeredEmail = ref('')
    const isResending = ref(false)

    const getCSRFToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : null
    }

    const handleSubmit = async () => {
      try {
        if (password.value !== passwordConfirmation.value) {
          error.value = 'Passwords do not match'
          return
        }

        isLoading.value = true
        error.value = ''

        const response = await fetch('/api/users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
          },
          body: JSON.stringify({
            user: {
              first_name: firstName.value,
              last_name: lastName.value,
              email: email.value,
              password: password.value,
              password_confirmation: passwordConfirmation.value
            }
          })
        })

        const data = await response.json()

        if (data.success) {
          registrationSuccess.value = true
          registeredEmail.value = email.value
          // Clear the form
          firstName.value = ''
          lastName.value = ''
          email.value = ''
          password.value = ''
          passwordConfirmation.value = ''
        } else {
          error.value = data.message || (data.errors && data.errors.join(', ')) || 'Registration failed'
        }
      } catch (err) {
        error.value = 'An error occurred. Please try again.'
        console.error('Signup error:', err)
      } finally {
        isLoading.value = false
      }
    }

    const resendConfirmation = async () => {
      try {
        isResending.value = true
        error.value = ''

        const response = await fetch('/api/users/resend_confirmation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
          },
          body: JSON.stringify({
            email: registeredEmail.value
          })
        })

        const data = await response.json()

        if (data.success) {
          // Optionally, show a success message or update the UI
          error.value = 'Confirmation email resent. Please check your inbox.'
        } else {
          error.value = data.message || 'Failed to resend confirmation email'
        }
      } catch (err) {
        error.value = 'An error occurred while resending confirmation email. Please try again.'
        console.error('Resend confirmation error:', err)
      } finally {
        isResending.value = false
      }
    }

    return {
      firstName,
      lastName,
      email,
      password,
      passwordConfirmation,
      isLoading,
      error,
      registrationSuccess,
      registeredEmail,
      isResending,
      handleSubmit,
      resendConfirmation,
      logoUrl
    }
  }
}
</script>

<style>
.logo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.logo {
  height: 4rem;
  width: auto;
}

@media (min-width: 640px) {
  .logo {
    height: 5rem;
  }
}
</style>